import { GridApi } from 'ag-grid-community';
import { Dispatch, ReactElement, SetStateAction, useCallback, useEffect, useState } from 'react';

import { TableLayoutType } from 'core/enums';
import { useAgGridScrollButtons } from 'core/hooks';
import { useSelectionCountContext } from 'core/states';
import {
  AgGridDataRow,
  IOption,
  ISelectionAction,
  ISelectionActionProps,
  ITableAction,
  ITableActionProps,
} from 'core/types';
import { trimFilterText } from 'core/utils';

import TopActionsView from './TopActionsView';
import { getColIds } from './utils';

export interface IProps {
  actions?: () => ITableAction[];
  actionButtons?: ReactElement[] | ((props: ITableActionProps) => ReactElement[]);
  rightActionButtons?: ReactElement[];
  selectionActions?: (props: ISelectionActionProps) => ISelectionAction[];
  selectionActionButtons?: ReactElement[] | ((props: ISelectionActionProps) => ReactElement[]);
  selectionType?: string;
  closeSelection?: boolean;
  setCloseSelection?: Dispatch<SetStateAction<boolean>>;
  tableId: string;
  gridApi?: GridApi;
  selectedRows: AgGridDataRow[];
  deselectAllRows?: () => void;
  isDataLoading: boolean;
  isDataLoadCompleted: boolean;
  layoutType: TableLayoutType;
  hasLayoutActions: boolean;
  hasManageFilters: boolean;
  resetMenuItems: IOption[];
  saveLayoutDisabled?: boolean;
  onSaveLayout?: () => void;
  hasQuickSearch?: boolean;
  hasGroupByEnabled?: boolean;
}

const TopActions = ({
  actions,
  actionButtons = [],
  rightActionButtons = [],
  selectionActions,
  selectionActionButtons = [],
  selectionType,
  closeSelection,
  setCloseSelection,
  tableId,
  gridApi,
  selectedRows,
  deselectAllRows,
  isDataLoading,
  isDataLoadCompleted,
  layoutType,
  hasLayoutActions,
  hasManageFilters,
  resetMenuItems,
  saveLayoutDisabled,
  onSaveLayout,
  hasQuickSearch,
  hasGroupByEnabled = false,
}: IProps) => {
  const [isSearchActive, setSearchActive] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  const { setSelectionCount } = useSelectionCountContext();

  const { isScrollLeftDisabled, isScrollRightDisabled, scroll } = useAgGridScrollButtons({
    tableId,
    innerElementColIds: getColIds(gridApi?.getColumnState() || []),
  });

  const onCloseSelection = useCallback(() => {
    gridApi?.deselectAll();
  }, [gridApi]);

  useEffect(() => {
    if (closeSelection) {
      onCloseSelection();
      setCloseSelection?.(false);
      setSelectionCount(0);
    }
  }, [closeSelection, onCloseSelection, setCloseSelection, setSelectionCount]);

  const onSearchIconClick = () => setSearchActive(true);

  const refreshData = useCallback(() => {
    if (!gridApi) {
      return;
    }

    if (gridApi.getGridOption('rowModelType') === 'serverSide') {
      gridApi.refreshServerSide({ purge: true });
    }
  }, [gridApi]);

  const onQuickFilterSubmit = useCallback(() => {
    gridApi?.setGridOption('quickFilterText', trimFilterText(searchText));
    refreshData();
  }, [gridApi, refreshData, searchText]);

  const onQuickFilterReset = useCallback(() => {
    gridApi?.setGridOption('quickFilterText', '');
    refreshData();

    setSearchActive(false);
    setSearchText('');
  }, [gridApi, refreshData]);

  const handleOnSelectionButtonClick = async (action: ISelectionAction) => {
    if (!action?.onClick) {
      return;
    }

    const success = await action?.onClick();

    if (!success) {
      return;
    }

    onCloseSelection();
  };

  return (
    <TopActionsView
      tableId={tableId}
      hasQuickSearch={hasQuickSearch}
      isSearchActive={isSearchActive}
      onSearchIconClick={onSearchIconClick}
      searchText={searchText}
      setSearchText={setSearchText}
      onQuickFilterSubmit={onQuickFilterSubmit}
      onQuickFilterReset={onQuickFilterReset}
      scroll={scroll}
      isScrollLeftDisabled={isScrollLeftDisabled}
      isScrollRightDisabled={isScrollRightDisabled}
      isDataLoading={isDataLoading}
      gridApi={gridApi}
      selectedRows={selectedRows}
      deselectAllRows={deselectAllRows}
      handleOnSelectionButtonClick={handleOnSelectionButtonClick}
      onCloseSelection={onCloseSelection}
      actions={actions}
      actionButtons={actionButtons}
      rightActionButtons={rightActionButtons}
      selectionActions={selectionActions}
      selectionActionButtons={selectionActionButtons}
      selectionType={selectionType}
      isTableLoadingCompleted={isDataLoadCompleted}
      saveLayoutDisabled={saveLayoutDisabled}
      onSaveLayout={onSaveLayout}
      layoutType={layoutType}
      hasLayoutActions={hasLayoutActions}
      hasManageFilters={hasManageFilters}
      resetMenuItems={resetMenuItems}
      hasGroupByEnabled={hasGroupByEnabled}
    />
  );
};

export default TopActions;
